<template>
  <v-card
    v-if="order"
    :min-width="minWidth"
  >
    <v-card-title
      :class="getOrderTypeColor(order)"
      class="white--text pa-2"
      style="cursor: pointer;"
      @click="onKdsOrderClick"
    >
      <v-container
        fluid
        pa-0
      >
        <v-row no-gutters>
          <v-col cols="12">
            <v-chip
              class="elevation-0 pl-0 pr-1"
              color="rgba(255, 255, 255, 0.10)"
              dark
              label
              small
              style="pointer-events: none;"
            >
              <v-avatar
                v-if="getOrderSourceTypeById(order.SourceTypeId)"
                class="mr-2"
                color="rgba(0, 0, 0, 0.20)"
                tile
              >
                <v-icon
                  v-if="order.SourceTypeId < 100"
                  color="white"
                  size="18"
                >
                  {{ getOrderSourceTypeById(order.SourceTypeId).logo }}
                </v-icon>

                <img
                  v-else
                  :src="getOrderSourceTypeById(order.SourceTypeId).logo"
                >
              </v-avatar>

              <div
                v-if="order.SourceTypeId === SourceTypeEnum.TABLE"
                class="font-weight-medium"
                style="font-size: 11px"
              >
                {{ order.TableArea }} #{{ order.TableName }}
              </div>

              <div v-else>
                #{{ order.OrderCode }}
              </div>
            </v-chip>

            <order-type-chip
              :hide-time="order.SourceTypeId === SourceTypeEnum.TABLE"
              :order="order"
              :tooltip="false"
              chip-class=""
              class="float-end"
              dense
              label
              min-width="135"
              style="pointer-events: none;"
            />
          </v-col>
        </v-row>

        <v-row no-gutters>
          <v-col
            class="text-start"
            cols="6"
          >
            <div v-if="order.DailyCheck && appConfig.LOCATION_DATA.ShowDailyCheck">
              <v-chip
                class="px-1"
                color="rgba(255, 255, 255, 0.10)"
                dark
                label
                small
                style="pointer-events: none;"
              >
                {{ $t('Order.Headers.DailyCheck') }}: {{ order.DailyCheck }}
              </v-chip>
            </div>

            <div
              v-if="order.StaffId && parseInt(order.SourceTypeId) === SourceTypeEnum.TABLE && order.Persons"
            >
              <v-chip
                class="px-1"
                color="rgba(255, 255, 255, 0.10)"
                dark
                label
                small
                style="pointer-events: none;"
              >
                <strong>{{ $t('Reservation.Table.Headers.Persons') }}:</strong>&nbsp;{{ order.Persons }}
              </v-chip>
            </div>
          </v-col>

          <v-col
            class="text-end"
            cols="6"
          >
            <v-chip
              class="px-1 justify-center"
              color="rgba(255, 255, 255, 0.10)"
              dark
              label
              small
              style="pointer-events: none;"
            >
              {{ order.TimeAdded }}
            </v-chip>

            <v-chip
              v-if="order.Status !== KdsOrderStatusEnum.CANCEL"
              class="px-1 justify-center ml-1"
              color="rgba(255, 255, 255, 0.10)"
              dark
              label
              small
              style="pointer-events: none; min-width: 70px;"
            >
              <time-ago-moment
                v-if="order.Status === KdsOrderStatusEnum.COMPLETE"
                :auto-update="0"
                :since="getOrderStatusTimeSince(order)"
                class="font-weight-bold"
              />

              <time-ago-moment
                v-else
                :auto-update="1"
                :since="getOrderAddedTimeSince(order)"
                class="font-weight-bold"
              />
            </v-chip>
          </v-col>
        </v-row>
      </v-container>
    </v-card-title>

    <v-card-text
      :style="fillHeight ? `overflow-y: auto; height: calc(100vh - ${hideOrderActions ? '248px' : '300px'})` : ''"
      class="px-1 py-0"
    >
      <template v-for="obj in order.Items">
        <div
          :key="`category-${obj.Category.Id}`"
          :style="'border-left: #' + (obj.Category.Color ? obj.Category.Color : 'e1e1e1') + ' 3px solid; ' + (isDarkMode ? 'background-color: #191919;' : 'background-color: #e1e1e1;')"
        >
          <v-subheader class="text-subtitle-1 pl-4 pt-2 pb-2">
            {{ getTranslatedField(obj.Category, 'Name') }}
          </v-subheader>
        </div>


        <template v-for="(item, itemIdx) in obj.Items">
          <div :key="`category-${obj.Category.Id}-item-${item.Id}-index-${itemIdx}`">
            <div class="my-2">
              <div class="text-body-2 font-weight-medium py-1 px-0">
                <div
                  class="d-flex align-center"
                  style="width: 100%;"
                >
                  <div
                    v-if="item.Status !== KdsOrderItemStatusEnum.OPEN"
                    class="mr-1 d-inline-flex"
                  >
                    <v-chip
                      :color="getKdsOrderItemStatus(item)?.Color"
                      class="px-0"
                      dark
                      label
                      x-small
                    >
                      <v-icon small>
                        {{ getKdsOrderItemStatus(item)?.Icon }}
                      </v-icon>
                    </v-chip>
                  </div>

                  <div class="orange--text mr-1 d-inline-flex">
                    <template v-if="item.UnitId > 1">
                      <span style="white-space: nowrap; min-width: 28px;">
                        {{ formatUnitQuantity($i18n.locale, item.UnitQuantity, getMeasurementUnitById(item.UnitId)) }}
                      </span>
                    </template>
                    <template v-else>
                      <span style="white-space: nowrap; min-width: 28px;">
                        {{ item.Quantity }} x
                      </span>
                    </template>
                  </div>

                  <div
                    class="d-inline-flex"
                    style="width: 100%;"
                  >
                    {{ getTranslatedField(item, 'Name') }}
                  </div>

                  <div
                    v-if="order.Status === KdsOrderStatusEnum.OPEN && !hideItemActions"
                    class="ml-2 d-inline-flex"
                  >
                    <v-btn
                      v-if="canStartKdsOrderItem(item)"
                      :color="$t('Kds.Action.Item.Start.Color')"
                      :disabled="loadingStartKdsOrderItem(item)"
                      :loading="loadingStartKdsOrderItem(item)"
                      class="ml-1 white--text"
                      depressed
                      min-width="0"
                      small
                      width="30"
                      @click="onKdsOrderItemStartClick(item, order)"
                    >
                      <v-icon>{{ $t('Kds.Action.Item.Start.Icon') }}</v-icon>
                    </v-btn>

                    <v-btn
                      v-if="canHoldKdsOrderItem(item)"
                      :color="$t('Kds.Action.Item.Hold.Color')"
                      :disabled="loadingHoldKdsOrderItem(item)"
                      :loading="loadingHoldKdsOrderItem(item)"
                      class="ml-1 white--text"
                      depressed
                      min-width="0"
                      small
                      width="30"
                      @click="onKdsOrderItemHoldClick(item, order)"
                    >
                      <v-icon>{{ $t('Kds.Action.Item.Hold.Icon') }}</v-icon>
                    </v-btn>

                    <v-btn
                      v-if="canRevertKdsOrderItem(item)"
                      :color="$t('Kds.Action.Item.Revert.Color')"
                      :disabled="loadingRevertKdsOrderItem(item)"
                      :loading="loadingRevertKdsOrderItem(item)"
                      class="ml-1 white--text"
                      depressed
                      min-width="0"
                      small
                      width="30"
                      @click="onKdsOrderItemRevertClick(item, order)"
                    >
                      <v-icon>{{ $t('Kds.Action.Item.Revert.Icon') }}</v-icon>
                    </v-btn>

                    <v-btn
                      v-if="canCompleteKdsOrderItem(item)"
                      :color="$t('Kds.Action.Item.Complete.Color')"
                      :disabled="loadingCompleteKdsOrderItem(item)"
                      :loading="loadingCompleteKdsOrderItem(item)"
                      class="ml-1 white--text"
                      depressed
                      min-width="0"
                      small
                      width="30"
                      @click="onKdsOrderItemCompleteClick(item, order)"
                    >
                      <v-icon>{{ $t('Kds.Action.Item.Complete.Icon') }}</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>

              <div
                v-if="getItemOptions(item)?.length"
                class="grey--text text--darken-1 ml-1 text-caption"
              >
                <template v-for="(option, idx) in getItemOptions(item)">
                  <div
                    v-if="getOptionValues(option)?.length"
                    :key="idx"
                    style="display: table-row-group; vertical-align: middle;"
                  >
                    <div
                      :style="`min-width:${item.UnitId > 1 ? 48 : 28}px`"
                      style="display: table-cell; vertical-align: middle; white-space: nowrap;"
                    />
                    <div
                      style="display: table-cell; vertical-align: middle; white-space: nowrap; width: 100%;"
                    >
                      <div
                        v-if="option.Display"
                        class="text-caption font-weight-bold pt-2 pb-1 grey--text text--darken-2 ml-1"
                      >
                        {{ getOptionGroup(option) }}
                      </div>

                      <template
                        v-for="(optionValue, odx) in getOptionValues(option)"
                      >
                        <div
                          :key="odx"
                          style="margin-left: 5px !important;"
                        >
                          • <span v-safe-html="optionValue" />
                        </div>
                      </template>
                    </div>
                  </div>
                </template>
              </div>

              <div
                v-if="item.Comment"
                :class="isDarkMode ? 'white--text' : 'black--text'"
                class="ml-1 text-caption"
              >
                {{ $t('Order.OrderDetailsDialog.Customer.Comments') }}
                <span class="grey--text text--darken-1">
                  {{ item.Comment }}
                </span>
              </div>
            </div>

            <div v-if="itemIdx+1 < obj.Items.length">
              <v-divider class="my-0" />
            </div>
          </div>
        </template>
      </template>
    </v-card-text>

    <template v-if="!hideOrderActions">
      <v-divider />

      <v-card-actions>
        <v-btn
          v-if="!canRevertKdsOrder(order) && !canResumeKdsOrder(order)"
          :color="$t('Kds.Action.Order.Complete.Color')"
          :disabled="!canCompleteKdsOrder(order)|| loadingCompleteKdsOrder(order)"
          :loading="loadingCompleteKdsOrder(order)"
          block
          class="white--text"
          depressed
          @click="onKdsOrderCompleteClick(order)"
        >
          <v-icon>
            {{ $t('Kds.Action.Order.Complete.Icon') }}
          </v-icon>

          <v-spacer />
          {{ $t('Kds.Action.Order.Complete.Name') }}
          <v-spacer />
        </v-btn>

        <v-btn
          v-if="canRevertKdsOrder(order)"
          :color="$t('Kds.Action.Order.Revert.Color')"
          :disabled="!canRevertKdsOrder(order) || loadingRevertKdsOrder(order)"
          :loading="loadingRevertKdsOrder(order)"
          block
          class="white--text"
          depressed
          @click="onKdsOrderRevertClick(order)"
        >
          <v-icon>
            {{ $t('Kds.Action.Order.Revert.Icon') }}
          </v-icon>

          <v-spacer />
          {{ $t('Kds.Action.Order.Revert.Name') }}
          <v-spacer />
        </v-btn>

        <v-btn
          v-if="canResumeKdsOrder(order)"
          :color="$t('Kds.Action.Order.Resume.Color')"
          :disabled="!canResumeKdsOrder(order) || loadingResumeKdsOrder(order)"
          :loading="loadingResumeKdsOrder(order)"
          block
          class="white--text"
          depressed
          @click="onKdsOrderResumeClick(order)"
        >
          <v-icon>
            {{ $t('Kds.Action.Order.Resume.Icon') }}
          </v-icon>

          <v-spacer />
          {{ $t('Kds.Action.Order.Resume.Name') }}
          <v-spacer />
        </v-btn>
      </v-card-actions>
    </template>
  </v-card>
</template>

<script>

import SourceTypeEnum from '@/api/Enums/SourceTypeEnum.js'
import OrderSourceType from '@/mixins/orders/sourceType.js'
import OrderType from '@/mixins/orders/orderType.js'
import OrderTypeChip from '@/components/orders/common/OrderTypeChip.vue'
import Translatable from '@/mixins/translatable.js'
import Auth from '@/mixins/auth.js'
import {formatUnitQuantity} from '@/lib/currency/currency.js'
import AppData from '@/mixins/appdata.js'
import KdsOrderCommon from '@/mixins/kds/kdsOrderCommon.js'
import KdsOrderActions from '@/mixins/kds/kdsOrderActions.js'
import OrderStatusEnum from '@/api/Enums/OrderStatusEnum.js'
import KdsOrderStatusEnum from '@/api/Enums/KdsOrderStatusEnum.js'
import KdsOrderItemStatusEnum from '@/api/Enums/KdsOrderItemStatusEnum.js'
import moment from 'moment'
import TimeAgoMoment from '@/components/common/TimeAgoMoment.js'

export default {
  name      : 'KdsOrderCard',
  components: {TimeAgoMoment, OrderTypeChip},
  directives: {},
  mixins    : [Auth, AppData, Translatable, OrderSourceType, OrderType, KdsOrderCommon, KdsOrderActions],
  props     : {
    order: {
      type   : Object,
      default: undefined
    },

    fillHeight: {
      type   : Boolean,
      default: false
    },

    hideOrderActions: {
      type   : Boolean,
      default: false
    },

    hideItemActions: {
      type   : Boolean,
      default: false
    },

    minWidth: {
      type   : [Number, String],
      default: 350
    }
  },
  data() {
    return {}
  },
  computed: {
    KdsOrderItemStatusEnum() {
      return KdsOrderItemStatusEnum
    },
    KdsOrderStatusEnum() {
      return KdsOrderStatusEnum
    },
    OrderStatusEnum() {
      return OrderStatusEnum
    },
    SourceTypeEnum() {
      return SourceTypeEnum
    },

    selectedOrder() {
      return this.order
    }
  },
  watch: {},
  beforeCreate() {
  },
  created() {
  },
  beforeMount() {
  },
  mounted() {
  },
  beforeUpdate() {
  },
  updated() {
  },
  beforeDestroy() {
  },
  destroyed() {
  },
  methods: {
    formatUnitQuantity,

    getOrderAddedTimeSince(order) {
      return moment(order.DateAdded + ' ' + order.TimeAdded, 'DD-MM-YYYY HH:mm:ss')
    },

    getOrderStatusTimeSince(order) {
      return moment(order.StatusDate + ' ' + order.StatusTime, 'DD-MM-YYYY HH:mm:ss')
    },

    getItemOptions(item) {
      if (!Array.isArray(item?.Options)) return []

      return item.Options
    },

    getOptionGroup(option) {
      if (!option?.Group) return ''

      return option.Group[this.$i18n.locale] || option.Group[this.defaultLanguage.Code] || option.Group[Object.keys(option.Group) && Object.keys(option.Group)[0]]
    },

    getOptionValues(option) {
      if (!option?.Values) return []

      return option.Values[this.$i18n.locale] || option.Values[this.defaultLanguage.Code] || option.Values[Object.keys(option.Values) && Object.keys(option.Values)[0]]
    },

    onKdsOrderClick() {
      this.$emit('on-kds-order-click', this.order)
    }
  }
}
</script>

<style scoped>

</style>
